import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { KakeboLogo } from "@/components/KakeboLogo";
import { motion } from "framer-motion";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="hidden lg:flex flex-col justify-between p-12 bg-primary text-primary-foreground relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "radial-gradient(circle at 20% 20%, white 1px, transparent 1px)", backgroundSize: "32px 32px" }} />
        <div className="flex items-center gap-3 relative z-10">
          <KakeboLogo size={44} />
          <span className="font-display font-bold text-xl">Kakebo</span>
        </div>
        <div className="relative z-10">
          <h1 className="font-display font-bold text-4xl leading-tight tracking-tight">
            Vos finances,<br />ensemble, en toute confidentialité.
          </h1>
          <p className="mt-5 text-primary-foreground/70 text-base max-w-md">
            Suivez vos dépenses mensuelles, planifiez vos budgets annuels et importez vos relevés — le tout dans un espace réservé à vous deux.
          </p>
        </div>
        <p className="relative z-10 text-primary-foreground/50 text-sm">Suivi budgétaire partagé du foyer</p>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <KakeboLogo size={40} />
            <span className="font-display font-bold text-lg">Kakebo</span>
          </div>
          <h2 className="font-display font-bold text-3xl tracking-tight">Connexion</h2>
          <p className="text-muted-foreground mt-2 text-sm">Accédez à votre espace budgétaire.</p>

          <form onSubmit={submit} className="mt-8 space-y-5" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="email">Adresse email</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                required placeholder="vous@exemple.com" data-testid="login-email-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Mot de passe</Label>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                required placeholder="••••••••" data-testid="login-password-input" />
            </div>
            {error && <p className="text-sm text-destructive" data-testid="login-error">{error}</p>}
            <Button type="submit" className="w-full h-11 text-base" disabled={loading} data-testid="login-submit-button">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Se connecter"}
            </Button>
          </form>
        </motion.div>
      </div>
    </div>
  );
}
